import api from '@js/actions/api';

import {mapStore} from '@js/stores/mapStore';

import Computation from '@js/modules/computation';

import {
    RideTypeToRouteType,
    DurationEstimationModes,
    DefaultDurationEstimationMode
} from '@js/components/modules/constants';

// persist: save the choice as the user's default activity (window.currentUserRideType across loads).
// Defaults to true for user-driven changes; pass {persist: false} for programmatic/init calls (e.g. the
// planner seeding its type from a prop) so they don't overwrite the stored default with a non-choice.
export const applyRideType = (rideType, {persist = true} = {}) => {
    mapStore.currentRideType = rideType;
    mapStore.currentRouteType = RideTypeToRouteType[rideType];

    mapStore.map?.setRouteType(mapStore.currentRouteType);

    // Previously persisted server-side as a side effect of the estimate-duration call, which no longer
    // runs client-side.
    if (persist) {
        saveUserSettings({default_ride_type: rideType});
    }
};

export const computeRideElevation = (elevationData, requestOptions = {}) => (
    api.post('/api/v1/rides/compute-elevation', {
        elevation: elevationData
    }, false, requestOptions)
);

export const computeRideDuration = (durationData, requestOptions = {}) => (
    api.post('/api/v1/rides/estimate-duration', {
        elevation: {
            paceProfile: mapStore.currentPaceProfile,
            ...durationData
        }
    }, false, requestOptions)
);

const SETTINGS_WRITE_DELAY = 800;

let _pendingSettings = null;
let _pendingSettingsUserId = null;
let _pendingSettingsTimer = null;
let _pendingSettingsWaiters = [];

// Persist a partial patch of the connected user's settings (default_ride_type, pace_profile…). Merged
// server-side (users#update_settings), so it never wipes the other stored keys. Fire-and-forget: a failed
// save must never block or break the UI, and is a no-op for anonymous visitors.
//
// Writes are coalesced: someone trying activities in the planner produced one PUT per click (Sentry
// flagged 12 in a single trace). Since the server merges patches, a burst only ever needed the merged
// result — so the pending patches are accumulated and flushed once. The trade-off is a window of up to
// SETTINGS_WRITE_DELAY where a tab closed mid-burst loses the preference; it is a preference, not data.
export const saveUserSettings = (settings) => {
    const userId = window.currentUserId;

    if (!userId || !settings || !Object.keys(settings).length) {
        return Promise.resolve();
    }

    // Pinned at call time: the flush happens later, and a logout in between would otherwise write to
    // /api/v1/users/null/settings.
    _pendingSettingsUserId = userId;
    _pendingSettings = {..._pendingSettings, ...settings};

    clearTimeout(_pendingSettingsTimer);

    // Every caller of a coalesced burst waits on the single write, so no promise is left dangling.
    const waiting = new Promise((resolve) => _pendingSettingsWaiters.push(resolve));

    _pendingSettingsTimer = setTimeout(() => {
        const patch = _pendingSettings;
        const patchUserId = _pendingSettingsUserId;
        const waiters = _pendingSettingsWaiters;

        _pendingSettings = null;
        _pendingSettingsUserId = null;
        _pendingSettingsWaiters = [];

        const written = api.update(`/api/v1/users/${patchUserId}/settings`, {settings: patch}).catch(() => null);

        waiters.forEach((resolve) => resolve(written));
    }, SETTINGS_WRITE_DELAY);

    return waiting;
};

export const saveUserPaceProfile = (paceProfile) => (
    paceProfile ? saveUserSettings({pace_profile: paceProfile}) : Promise.resolve()
);

export const estimateRideDurationLocally = ({
                                                rideType,
                                                distance,
                                                paceProfile
                                            } = {}) => {
    if (!distance) {
        return Promise.resolve(null);
    }

    const build = (coordinateElevations) => {
        if (!coordinateElevations?.length) {
            return null;
        }

        const elevationByDistance = coordinateElevations.map((coordEl) => [coordEl.deltaDistance, coordEl.elevation]);
        const duration = Computation.estimateServerDuration(rideType, distance, elevationByDistance, {
            paceProfile: paceProfile || mapStore.currentPaceProfile
        });

        return {
            duration,
            chronoDuration: Computation.formatDuration(duration, {chronoFormat: true})
        };
    };

    const cached = mapStore.coordinateElevations;
    if (cached?.length) {
        return Promise.resolve(build(cached));
    }

    const map = mapStore.map;
    if (!map || typeof map.computeElevation !== 'function') {
        return Promise.resolve(null);
    }

    return new Promise((resolve) => {
        let settled = false;
        const done = (result) => {
            if (settled) {
                return;
            }
            settled = true;
            resolve(result);
        };

        const timer = setTimeout(() => done(null), 4000);

        map.computeElevation((coordinateElevations) => {
            clearTimeout(timer);
            done(build(coordinateElevations));
        });
    });
};

export const getDurationEstimationMode = () => (
    window.durationEstimationMode === DurationEstimationModes.SERVER
        ? DurationEstimationModes.SERVER
        : DefaultDurationEstimationMode
);

export const estimateRideDuration = ({
                                         rideType,
                                         distance,
                                         paceProfile
                                     } = {}) => {
    if (getDurationEstimationMode() === DurationEstimationModes.SERVER) {
        return computeRideDuration({
            mapPoints: mapStore.map?.serializePoints?.(),
            distance,
            rideType
        });
    }

    return estimateRideDurationLocally({
        rideType,
        distance,
        paceProfile
    });
};
